import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  FormHelperText,
  Input,
  Textarea,
  VStack,
  HStack,
  Text,
  Grid,
  Spinner,
  Select,
} from '@chakra-ui/react'
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import {
  createDepartmentSchema,
  updateDepartmentSchema,
  type CreateDepartmentFormData,
  type UpdateDepartmentFormData,
} from '@/lib/validations/auth'
import { sanitizeRestrictedLabelInput } from '@/lib/validations/restrictedLabel'

interface DepartmentFormProps {
  departmentId?: number
  onSuccess?: () => void
  onCancel?: () => void
}

type DepartmentFormValues = CreateDepartmentFormData | UpdateDepartmentFormData

interface ParentOption {
  id: number
  name?: string
  label?: string
}

export function DepartmentForm({
  departmentId,
  onSuccess,
  onCancel,
}: DepartmentFormProps) {
  const navigate = useNavigate()
  const { showError, showSuccess } = useToastNotification()

  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [parentOptions, setParentOptions] = useState<ParentOption[]>([])

  const isEditMode = !!departmentId

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = useForm<DepartmentFormValues>({
    resolver: zodResolver(
      isEditMode ? updateDepartmentSchema : createDepartmentSchema
    ),
    defaultValues: {
      name: '',
      code: '',
      descriptions: '',
      parent: undefined,
    },
  })

  const normalizeTitleCase = (value: string): string => {
    return value
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase())
  }

  /**
   * Load parent/division options
   */
  const loadParentOptions = useCallback(() => {
    apiClient
      .get('/division-management/divisions/dropdown')
      .then(({ data }) => {
        const divisionData = data?.data || data || []

        setParentOptions(
          Array.isArray(divisionData) ? divisionData : []
        )
      })
      .catch((error: any) => {
        showError(error)
        setParentOptions([])
      })
  }, [showError])

  /**
   * Load parent options
   */
  useEffect(() => {
    loadParentOptions()
  }, [loadParentOptions])

  /**
   * Load department when editing
   */
  useEffect(() => {
    if (isEditMode && departmentId) {
      setIsLoading(true)

      apiClient
        .get(`/departments/${departmentId}`)
        .then(({ data }) => {
          const department = data?.data || data || {}

          const parentId =
            department.division_id ??
            department.division?.id ??
            department.parent_id ??
            department.parent?.id ??
            null

          reset({
            name: sanitizeRestrictedLabelInput(department.name || ''),
            code: sanitizeRestrictedLabelInput(department.code || ''),
            descriptions: department.descriptions
              ? sanitizeRestrictedLabelInput(department.descriptions)
              : '',
            parent: parentId ?? undefined,
          })
        })
        .catch((error: any) => {
          showError(error)
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [departmentId, isEditMode, reset, showError])

  const onSubmit = async (data: DepartmentFormValues) => {
    setIsSubmitting(true)

    try {
      const payload = {
        name: normalizeTitleCase(sanitizeRestrictedLabelInput(data.name)),
        code: sanitizeRestrictedLabelInput(data.code).toUpperCase(),
        descriptions: data.descriptions
          ? normalizeTitleCase(sanitizeRestrictedLabelInput(data.descriptions)) || null
          : null,
        parent: data.parent ?? null,
      }

      if (isEditMode) {
        await apiClient.put(`/departments/${departmentId}`, payload)
        showSuccess('Success', 'Department updated successfully')
      } else {
        await apiClient.post('/departments', payload)
        showSuccess('Success', 'Department created successfully')
      }

      onSuccess?.() ?? navigate('/division-management/departments')
    } catch (error: any) {
      showError(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    } else {
      navigate('/division-management/departments')
    }
  }

  if (isLoading) {
    return (
      <VStack spacing={4} py={8}>
        <Spinner size="xl" color="blue.500" />
        <Text>Loading department...</Text>
      </VStack>
    )
  }

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing={4} align="stretch">

        {/* Name */}
        <Grid
          templateColumns={{ base: '1fr', md: '3fr 9fr' }}
          gap={4}
        >
          <FormLabel pt={2} fontWeight="medium">
            Name <Text as="span" color="red.500">*</Text>
          </FormLabel>

          <FormControl isInvalid={!!errors.name}>
            <Input
              {...register('name')}
              placeholder="e.g. Human Resources"
            />

            <FormErrorMessage>
              {errors.name?.message}
            </FormErrorMessage>

            <FormHelperText>
              The department name.
            </FormHelperText>
          </FormControl>
        </Grid>

        {/* Code */}
        <Grid
          templateColumns={{ base: '1fr', md: '3fr 9fr' }}
          gap={4}
        >
          <FormLabel pt={2} fontWeight="medium">
            Code <Text as="span" color="red.500">*</Text>
          </FormLabel>

          <FormControl isInvalid={!!errors.code}>
            <Input
              {...register('code')}
              placeholder="e.g. HR, IT, FIN"
            />

            <FormErrorMessage>
              {errors.code?.message}
            </FormErrorMessage>

            <FormHelperText>
              Short identifier for the department.
            </FormHelperText>
          </FormControl>
        </Grid>

        {/* Description */}
        <Grid
          templateColumns={{ base: '1fr', md: '3fr 9fr' }}
          gap={4}
        >
          <FormLabel pt={2} fontWeight="medium">
            Description
          </FormLabel>

          <FormControl isInvalid={!!errors.descriptions}>
            <Textarea
              {...register('descriptions')}
              placeholder="Enter department description..."
              rows={4}
            />

            <FormErrorMessage>
              {errors.descriptions?.message}
            </FormErrorMessage>

            <FormHelperText>
              Optional description for this department.
            </FormHelperText>
          </FormControl>
        </Grid>

        {/* Parent */}
        <Grid
          templateColumns={{ base: '1fr', md: '3fr 9fr' }}
          gap={4}
          alignItems="start"
        >
          <FormLabel pt={2} fontWeight="medium">
            Parent <Text as="span" color="red.500">*</Text>
          </FormLabel>

          <FormControl isInvalid={!!errors.parent}>
            <Select
              {...register('parent', {
                setValueAs: (value) =>
                  value === '' ? undefined : Number(value),
              })}
              placeholder="Select Parent"
            >
              {parentOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name ?? option.label}
                </option>
              ))}
            </Select>

            <FormErrorMessage>
              {errors.parent?.message}
            </FormErrorMessage>

            <FormHelperText>
              Assign this department to a parent.
            </FormHelperText>
          </FormControl>
        </Grid>

        {/* Actions */}
        <HStack spacing={4} justify="flex-end" pt={4}>
          <Button
            variant="outline"
            onClick={handleCancel}
            isDisabled={isSubmitting}
          >
            Cancel
          </Button>

          <Button
            type="submit"
            colorScheme="blue"
            isLoading={isSubmitting}
            loadingText={isEditMode ? 'Saving...' : 'Creating...'}
          >
            {isEditMode ? 'Save' : 'Create Department'}
          </Button>
        </HStack>

      </VStack>
    </Box>
  )
}